import React, { useState, useRef, useEffect, useCallback } from 'react';

const INITIAL_CROP_RATIO = 0.6;
const MIN_SIZE = 40;
/** Touch target size in CSS pixels (scaled to canvas coordinates). */
const HIT_RADIUS_SCREEN = 36;

function createCenteredCrop(canvasWidth, canvasHeight) {
  const width = canvasWidth * INITIAL_CROP_RATIO;
  const height = canvasHeight * INITIAL_CROP_RATIO;
  return {
    x: (canvasWidth - width) / 2,
    y: (canvasHeight - height) / 2,
    width,
    height,
  };
}

function normalizeCrop(crop, canvasWidth, canvasHeight) {
  let { x, y, width, height } = crop;

  if (width < 0) {
    x += width;
    width = Math.abs(width);
  }
  if (height < 0) {
    y += height;
    height = Math.abs(height);
  }

  width = Math.max(MIN_SIZE, Math.min(width, canvasWidth));
  height = Math.max(MIN_SIZE, Math.min(height, canvasHeight));
  x = Math.max(0, Math.min(x, canvasWidth - width));
  y = Math.max(0, Math.min(y, canvasHeight - height));

  return { x, y, width, height };
}

function getCanvasScale(canvas) {
  const rect = canvas.getBoundingClientRect();
  return canvas.width / rect.width;
}

function getHitRadius(canvas) {
  return HIT_RADIUS_SCREEN * getCanvasScale(canvas);
}

/** Dot centers sit on the four corners of the crop square. */
function getCornerHandles(crop) {
  return [
    { corner: 'tl', x: crop.x, y: crop.y },
    { corner: 'tr', x: crop.x + crop.width, y: crop.y },
    { corner: 'bl', x: crop.x, y: crop.y + crop.height },
    { corner: 'br', x: crop.x + crop.width, y: crop.y + crop.height },
  ];
}

function getCornerHit(x, y, crop, hit) {
  const handles = getCornerHandles(crop);
  for (const h of handles) {
    if (Math.abs(x - h.x) < hit && Math.abs(y - h.y) < hit) {
      return h.corner;
    }
  }
  return null;
}

function isInsideCrop(x, y, crop, hit) {
  const pad = hit * 0.85;
  return (
    x > crop.x + pad &&
    x < crop.x + crop.width - pad &&
    y > crop.y + pad &&
    y < crop.y + crop.height - pad
  );
}

function resizeFromCornerDelta(corner, dx, dy, startCrop, canvasWidth, canvasHeight) {
  let { x, y, width, height } = startCrop;

  switch (corner) {
    case 'tl':
      x += dx;
      y += dy;
      width -= dx;
      height -= dy;
      break;
    case 'tr':
      y += dy;
      width += dx;
      height -= dy;
      break;
    case 'bl':
      x += dx;
      width -= dx;
      height += dy;
      break;
    case 'br':
      width += dx;
      height += dy;
      break;
    default:
      break;
  }

  return normalizeCrop({ x, y, width, height }, canvasWidth, canvasHeight);
}

export const ImageCropper = ({ imgSrc, canvasRef, setCrop }) => {
  const imgRef = useRef(null);
  const [crop, setLocalCrop] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [canvasSize, setCanvasSize] = useState({ w: 1, h: 1 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null);
  const dragStartRef = useRef({ pointerX: 0, pointerY: 0, crop: null });

  const paintCrop = useCallback(
    (cropRect) => {
      const canvas = canvasRef.current;
      const img = imgRef.current;
      if (!canvas || !img || !cropRect?.width) return null;

      const ctx = canvas.getContext('2d');
      const normalized = normalizeCrop(cropRect, canvas.width, canvas.height);
      const { x: drawX, y: drawY, width: drawW, height: drawH } = normalized;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.drawImage(img, drawX, drawY, drawW, drawH, drawX, drawY, drawW, drawH);

      ctx.strokeStyle = 'white';
      ctx.lineWidth = 3;
      ctx.strokeRect(drawX, drawY, drawW, drawH);

      return normalized;
    },
    [canvasRef]
  );

  const applyCrop = useCallback(
    (cropRect) => {
      const normalized = paintCrop(cropRect);
      if (!normalized) return;
      const canvas = canvasRef.current;
      if (canvas) {
        setCanvasSize({ w: canvas.width, h: canvas.height });
      }
      setLocalCrop(normalized);
      setCrop(normalized);
    },
    [paintCrop, setCrop]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgSrc) return;

    const initFromImage = () => {
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      applyCrop(createCenteredCrop(canvas.width, canvas.height));
    };

    img.onload = initFromImage;
    img.src = imgSrc;
    if (img.complete && img.naturalWidth) {
      initFromImage();
    }
  }, [imgSrc, canvasRef, applyCrop]);

  const getCanvasCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = event.touches ? event.touches[0].clientX : event.clientX;
    const clientY = event.touches ? event.touches[0].clientY : event.clientY;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const onPointerDown = (event) => {
    event.preventDefault();
    const { x, y } = getCanvasCoordinates(event);
    const canvas = canvasRef.current;
    const hit = getHitRadius(canvas);
    const corner = getCornerHit(x, y, crop, hit);

    if (corner) {
      dragStartRef.current = { pointerX: x, pointerY: y, crop: { ...crop } };
      setDragMode(corner);
      setIsDragging(true);
      return;
    }

    if (isInsideCrop(x, y, crop, hit)) {
      dragStartRef.current = { pointerX: x, pointerY: y, crop: { ...crop } };
      setDragMode('move');
      setIsDragging(true);
    }
  };

  const onPointerMove = (event) => {
    if (!isDragging || !dragMode) return;

    const { x, y } = getCanvasCoordinates(event);
    const canvas = canvasRef.current;
    const start = dragStartRef.current;
    const dx = x - start.pointerX;
    const dy = y - start.pointerY;

    if (dragMode === 'move') {
      applyCrop({
        x: start.crop.x + dx,
        y: start.crop.y + dy,
        width: start.crop.width,
        height: start.crop.height,
      });
    } else {
      applyCrop(
        resizeFromCornerDelta(
          dragMode,
          dx,
          dy,
          start.crop,
          canvas.width,
          canvas.height
        )
      );
    }
  };

  const onPointerUp = () => {
    setIsDragging(false);
    setDragMode(null);
  };

  const cornerHandles = crop.width > 0 ? getCornerHandles(crop) : [];

  return (
    <div className="scan-cropper-wrap">
      <div className="scan-cropper-stage">
        <canvas
          ref={canvasRef}
          className="scan-cropper-canvas"
          style={{ cursor: isDragging && dragMode === 'move' ? 'grabbing' : 'crosshair' }}
          onMouseDown={onPointerDown}
          onMouseMove={onPointerMove}
          onMouseUp={onPointerUp}
          onMouseLeave={onPointerUp}
          onTouchStart={onPointerDown}
          onTouchMove={onPointerMove}
          onTouchEnd={onPointerUp}
        />
        {cornerHandles.length > 0 && (
          <div className="scan-cropper-handles" aria-hidden="true">
            {cornerHandles.map((h) => (
              <span
                key={h.corner}
                className="scan-cropper-handle"
                style={{
                  left: `${(h.x / canvasSize.w) * 100}%`,
                  top: `${(h.y / canvasSize.h) * 100}%`,
                }}
              />
            ))}
          </div>
        )}
      </div>
      <img ref={imgRef} src={imgSrc} alt="" style={{ display: 'none' }} />
    </div>
  );
};
